import { useQuery, useQueryClient } from '@tanstack/react-query';
import { differenceBy, intersectionBy, unionBy } from 'lodash';
import keyBy from 'lodash/keyBy';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { ProjectId, UserStatus, UserType } from 'api/models';
import { PartnerPermissionName } from 'api/partner/types';
import { MembersAPI } from 'data/members/api';
import { TemplateObjectType } from 'data/templates/api';
import { useUser } from 'hooks/use-user';
import { useProjectMembersQuery, useProjectPartnersMappingQuery } from 'queries';
import { useProjectsFromInitData } from 'queries/account';
import { AppUpgradeConfirmationKey } from 'queries/app-upgrade-confirmation';
import { useMembersV2TransformedQuery, useTeamsTransformedQuery } from 'queries/members';
import { useProjectDetailQuery } from 'queries/project';
import { logger } from 'services';
import { fullName } from 'utils/full-name';
import { getThumbnailUrl } from 'utils/get-thumbnail-url';
import { unreachable } from 'utils/unreachable';
import { db } from 'workers';
import { migrations } from 'workers/migrations';
import { useProjectAsRBACResource, useWithAccess } from './rbac/useWithAccess';
export const useInitIndexDbListeners = ()=>{
    const queryClient = useQueryClient();
    db.on('ready', (e)=>{
        logger.log('db ready with version', e === null || e === void 0 ? void 0 : e.verno);
    });
    db.on('close', (e)=>{
        logger.log('db close event fired', e);
    });
    db.on('versionchange', (e)=>{
        /**
     * versionchange event is fired in any open windows when a new window is opened which connects to a different version of indexD
     */ logger.log('db versionchange', e);
        queryClient.setQueryData(AppUpgradeConfirmationKey, true);
    });
};
const filterNonCustomerUsers = (user)=>{
    return user.userType !== UserType.External;
};
// export function usePrerenderMembersAndTeamsFromIndexedDb(): void {
//   const { data: membersData } = useMembersV2TransformedQuery();
//   const members = membersData?.users ?? [];
//   const teams = membersData?.teams ?? [];
//   activeUsersAndTeamsFromIndexedB.setActiveUsersAndTeams = {
//     users: members || [],
//     teams: teams || [],
//   };
// }
/**
 * @deprecated
 */ export function useGetAllNativeUsersFromIndexedDb() {
    const { data: membersData } = useMembersV2TransformedQuery();
    return useMemo(()=>{
        var _ref;
        const data = ((_ref = membersData === null || membersData === void 0 ? void 0 : membersData.users) !== null && _ref !== void 0 ? _ref : []).filter((x)=>x.userType === UserType.Native);
        return data;
    }, [
        membersData === null || membersData === void 0 ? void 0 : membersData.users
    ]);
}
/**
 * @deprecated
 */ export function useGetAllActiveNativeUsersFromIndexedDb() {
    const { data: membersData, isLoading: isMembersDataLoading } = useMembersV2TransformedQuery();
    return useMemo(()=>{
        var _ref;
        const data = ((_ref = membersData === null || membersData === void 0 ? void 0 : membersData.users) !== null && _ref !== void 0 ? _ref : []).filter((x)=>x.userType === UserType.Native && x.userStatus !== UserStatus.Inactive);
        return {
            users: data,
            isLoading: isMembersDataLoading
        };
    }, [
        membersData === null || membersData === void 0 ? void 0 : membersData.users,
        isMembersDataLoading
    ]);
}
/**
 * @deprecated
 */ export function useGetAllActivePartnerUsersFromIndexedDb() {
    const { data: membersData } = useMembersV2TransformedQuery();
    return useMemo(()=>{
        var _ref;
        const data = ((_ref = membersData === null || membersData === void 0 ? void 0 : membersData.users) !== null && _ref !== void 0 ? _ref : []).filter((x)=>x.userType === UserType.Partner);
        return data;
    }, [
        membersData === null || membersData === void 0 ? void 0 : membersData.users
    ]);
}
export function useGetActiveOrInvitedNonCustomerUsersFromIndexedDb() {
    const { data: membersData } = useMembersV2TransformedQuery();
    return useMemo(()=>{
        var _ref;
        const data = ((_ref = membersData === null || membersData === void 0 ? void 0 : membersData.users) !== null && _ref !== void 0 ? _ref : []).filter(filterNonCustomerUsers);
        return data;
    }, [
        membersData === null || membersData === void 0 ? void 0 : membersData.users
    ]);
}
/**
 * @deprecated
 */ export function useGetUserByUserId(userId) {
    const { data: membersData } = useMembersV2TransformedQuery();
    return useMemo(()=>{
        var _ref;
        if (!userId) {
            return undefined;
        }
        const data = ((_ref = membersData === null || membersData === void 0 ? void 0 : membersData.users) !== null && _ref !== void 0 ? _ref : []).find((x)=>x.userId === userId);
        return data;
    }, [
        membersData === null || membersData === void 0 ? void 0 : membersData.users,
        userId
    ]);
}
export function useGetTeamsByProjectId(projectIds, enabled = true) {
    const { data: membersData, isLoading: isMembersDataLoading } = useMembersV2TransformedQuery();
    const getData = useCallback(()=>{
        const teams = membersData === null || membersData === void 0 ? void 0 : membersData.teams;
        if (enabled === false) {
            return undefined;
        }
        const teamsByProjectIds = {};
        projectIds.forEach((projectId)=>{
            const teamList = (teams !== null && teams !== void 0 ? teams : []).filter((t)=>{
                var _t_projectIds_includes, _t_projectIds;
                return (_t_projectIds = t.projectIds) === null || _t_projectIds === void 0 ? void 0 : (_t_projectIds_includes = _t_projectIds.includes) === null || _t_projectIds_includes === void 0 ? void 0 : _t_projectIds_includes.call(_t_projectIds, projectId);
            });
            teamsByProjectIds[projectId] = teamList;
        });
        return teamsByProjectIds;
    }, [
        enabled,
        membersData === null || membersData === void 0 ? void 0 : membersData.teams,
        projectIds
    ]);
    return useMemo(()=>{
        const data = getData();
        return data != null ? {
            data,
            isLoading: isMembersDataLoading
        } : {
            data: {},
            isLoading: true
        };
    }, [
        getData
    ]);
}
export function useGetTeamsByProjectIdV2() {
    const { data: teams, isLoading: isTeamsDataLoading } = useTeamsTransformedQuery();
    const getData = useCallback(()=>{
        const teamsByProjectIds = {};
        (teams !== null && teams !== void 0 ? teams : []).forEach((team)=>{
            var _team_projectIds;
            ((_team_projectIds = team.projectIds) !== null && _team_projectIds !== void 0 ? _team_projectIds : []).forEach((projectId)=>{
                if (!teamsByProjectIds[projectId]) {
                    teamsByProjectIds[projectId] = [];
                }
                teamsByProjectIds[projectId].push(team);
            });
        });
        return teamsByProjectIds;
    }, [
        teams
    ]);
    return useMemo(()=>{
        const data = getData();
        return data != null ? {
            data,
            isLoading: isTeamsDataLoading
        } : {
            data: {},
            isLoading: true
        };
    }, [
        getData
    ]);
}
// having this to avoid circular dependency
export const useProjectDetail = (projectId, options = {})=>{
    return useProjectDetailQuery(projectId, {
        ...options,
        retry: 1,
        retryOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
        enabled: typeof (projectId === null || projectId === void 0 ? void 0 : projectId[1]) === 'number' && (projectId === null || projectId === void 0 ? void 0 : projectId[1]) !== -1
    });
};
export function useGetAllMembersInProject({ isPrivate = false, projectId, options = {
    includeRestricedPartnersMembers: true
} }) {
    const { data: project } = useProjectDetail(ProjectId(projectId !== null && projectId !== void 0 ? projectId : -1));
    const projectMembers = useProjectMembersQuery([
        'project_id',
        projectId !== null && projectId !== void 0 ? projectId : -1
    ]);
    const membersData = useMemo(()=>{
        var _ref, _ref1;
        var _projectMembers_data, _projectMembers_data1;
        return {
            users: (_ref = projectMembers === null || projectMembers === void 0 ? void 0 : (_projectMembers_data = projectMembers.data) === null || _projectMembers_data === void 0 ? void 0 : _projectMembers_data.userList) !== null && _ref !== void 0 ? _ref : [],
            teams: (_ref1 = projectMembers === null || projectMembers === void 0 ? void 0 : (_projectMembers_data1 = projectMembers.data) === null || _projectMembers_data1 === void 0 ? void 0 : _projectMembers_data1.teamList) !== null && _ref1 !== void 0 ? _ref1 : []
        };
    }, [
        projectMembers
    ]);
    const permissivePartnerCompanyIdsInProject = useMemo(()=>{
        var _ref;
        return ((_ref = project === null || project === void 0 ? void 0 : project.partners) !== null && _ref !== void 0 ? _ref : []).filter((partner)=>{
            var _partner_accessLevel;
            return ((_partner_accessLevel = partner.accessLevel) === null || _partner_accessLevel === void 0 ? void 0 : _partner_accessLevel.partnerPermissionSetName) === PartnerPermissionName.Permissive;
        }).map((partner)=>partner.companyId);
    }, [
        project === null || project === void 0 ? void 0 : project.partners
    ]);
    const restricedPartnerCompanyIdsInProject = useMemo(()=>{
        var _ref;
        return ((_ref = project === null || project === void 0 ? void 0 : project.partners) !== null && _ref !== void 0 ? _ref : []).filter((partner)=>{
            var _partner_accessLevel;
            return ((_partner_accessLevel = partner.accessLevel) === null || _partner_accessLevel === void 0 ? void 0 : _partner_accessLevel.partnerPermissionSetName) === PartnerPermissionName.Restricted;
        }).map((partner)=>partner.companyId);
    }, [
        project === null || project === void 0 ? void 0 : project.partners
    ]);
    const filterForPrivateResource = useCallback((user)=>user.userType === UserType.Native || (user.userType === UserType.Partner || user.userType === UserType.CustomerPartner) && permissivePartnerCompanyIdsInProject.includes(user.company.companyId), [
        permissivePartnerCompanyIdsInProject
    ]);
    const filterOutRestrictedPartners = useCallback((user)=>{
        return user.userType !== UserType.CustomerPartner || !restricedPartnerCompanyIdsInProject.includes(user.company.companyId);
    }, [
        restricedPartnerCompanyIdsInProject
    ]);
    const conditionalFilter = useCallback((user)=>{
        return (options.includeRestricedPartnersMembers === true || filterOutRestrictedPartners(user)) && (isPrivate ? filterForPrivateResource(user) : true);
    }, [
        filterForPrivateResource,
        filterOutRestrictedPartners,
        isPrivate,
        options.includeRestricedPartnersMembers
    ]);
    const data = useMemo(()=>{
        var _ref;
        if (!projectId) {
            return [];
        }
        return ((_ref = membersData === null || membersData === void 0 ? void 0 : membersData.users) !== null && _ref !== void 0 ? _ref : []).filter(conditionalFilter);
    }, [
        projectId,
        membersData,
        conditionalFilter
    ]);
    return {
        data: data || [],
        isLoading: projectMembers.isLoading
    };
}
/**
 * @deprecated
 */ export function useGetAllUsers({ includeDeactivatedUsers = false }) {
    const { data: membersData } = useMembersV2TransformedQuery();
    return useMemo(()=>{
        var _ref;
        const data = ((_ref = membersData === null || membersData === void 0 ? void 0 : membersData.users) !== null && _ref !== void 0 ? _ref : []).filter((user)=>{
            return includeDeactivatedUsers ? true : user.userStatus !== UserStatus.Inactive;
        });
        return data;
    }, [
        includeDeactivatedUsers,
        membersData === null || membersData === void 0 ? void 0 : membersData.users
    ]);
}
// Async version of useGetAllUsers
export function useAsyncAccountUsers() {
    const userOption = useCallback((user)=>({
            label: fullName(user),
            value: user.userId,
            imageUrl: getThumbnailUrl(user.photoUrl),
            userStatus: user.userStatus,
            extra: {
                id: user.userId,
                type: 'user',
                role: user.role,
                userType: user.userType,
                company: user.company
            }
        }), []);
    // Fetch initial data at account-level
    const { data: membersData, isLoading } = useQuery([
        'account-users'
    ], async ()=>{
        const data = await MembersAPI.fetchPaginatedMembersData({
            projectId: -1,
            userStatus: [
                UserStatus.Active,
                UserStatus.Invited
            ],
            startRow: 0,
            endRow: 50
        });
        return data;
    });
    // Filter users based on basic rules
    const filterUsers = useCallback((users)=>{
        return users.filter((user)=>{
            // Rule 1: Filter out External users
            if (user.userType === UserType.External) {
                return false;
            }
            // Rule 2: Only Active or Invited (custom fields don't show inactive)
            if (user.userStatus === UserStatus.Inactive) {
                return false;
            }
            // Rule 3: Filter out API key users (UUID emails without @)
            if (user.emailId && !user.emailId.includes('@')) {
                return false;
            }
            return true;
        });
    }, []);
    // Default options from initial fetch
    const defaultOptions = useMemo(()=>{
        if (!membersData) {
            return [];
        }
        // Combine all account users (not project-specific)
        const allUsers = [
            ...membersData.inProjectUsers,
            ...membersData.notInProjectUsers
        ];
        return filterUsers(allUsers).map(userOption);
    }, [
        membersData,
        filterUsers,
        userOption
    ]);
    // Async load options for search
    const loadOptions = useCallback(async (inputValue)=>{
        try {
            const data = await MembersAPI.fetchPaginatedMembersData({
                projectId: -1,
                searchString: inputValue,
                userStatus: [
                    UserStatus.Active,
                    UserStatus.Invited
                ],
                startRow: 0,
                endRow: 100
            });
            // Combine all account users
            const allUsers = [
                ...data.inProjectUsers,
                ...data.notInProjectUsers
            ];
            return filterUsers(allUsers).map(userOption);
        } catch (_error) {
            return [];
        }
    }, [
        filterUsers,
        userOption
    ]);
    return {
        defaultOptions,
        loadOptions,
        isLoading
    };
}
export function useGetAllActiveUsersMap() {
    const { data: membersData } = useMembersV2TransformedQuery();
    return useMemo(()=>{
        var _ref;
        const users = ((_ref = membersData === null || membersData === void 0 ? void 0 : membersData.users) !== null && _ref !== void 0 ? _ref : []).filter((user)=>user.userStatus !== UserStatus.Inactive);
        return keyBy(users, 'userId');
    }, [
        membersData === null || membersData === void 0 ? void 0 : membersData.users
    ]);
}
export function useGetAllNativeUsersMap() {
    const { data: membersData } = useMembersV2TransformedQuery();
    return useMemo(()=>{
        var _ref;
        const users = ((_ref = membersData === null || membersData === void 0 ? void 0 : membersData.users) !== null && _ref !== void 0 ? _ref : []).filter((user)=>user.userType === UserType.Native);
        return keyBy(users, 'userId');
    }, [
        membersData === null || membersData === void 0 ? void 0 : membersData.users
    ]);
}
export function useGetAllNativeAndPartnerUsersMap() {
    const { data: membersData } = useMembersV2TransformedQuery();
    return useMemo(()=>{
        var _ref;
        const users = ((_ref = membersData === null || membersData === void 0 ? void 0 : membersData.users) !== null && _ref !== void 0 ? _ref : []).filter((user)=>user.userType !== UserType.External);
        return keyBy(users, 'userId');
    }, [
        membersData === null || membersData === void 0 ? void 0 : membersData.users
    ]);
}
export function useGetAllNativeActiveUsersMap() {
    const { data: membersData } = useMembersV2TransformedQuery();
    return useMemo(()=>{
        var _ref;
        const users = ((_ref = membersData === null || membersData === void 0 ? void 0 : membersData.users) !== null && _ref !== void 0 ? _ref : []).filter((user)=>user.userStatus !== UserStatus.Inactive && user.userType === UserType.Native);
        return keyBy(users, 'userId');
    }, [
        membersData === null || membersData === void 0 ? void 0 : membersData.users
    ]);
}
const useFilterForUserInviteableInProject = ()=>{
    const { user: currentUser } = useUser();
    const getFilterUserInviteableInProject = useCallback((project, projectLight, isPrivate, options = {
        includeRestricedPartnersMembers: true
    })=>{
        var _ref, _ref1, _ref2, _ref3;
        const customerCompanyId = projectLight === null || projectLight === void 0 ? void 0 : projectLight.customer.companyId;
        const currentUserCompanyFromProjectPartners = currentUser.userType === UserType.Partner ? ((_ref = project === null || project === void 0 ? void 0 : project.partners) !== null && _ref !== void 0 ? _ref : []).find((partner)=>partner.companyId === currentUser.company.companyId) : undefined;
        const partnersInProject = ((_ref1 = project === null || project === void 0 ? void 0 : project.partners) !== null && _ref1 !== void 0 ? _ref1 : []).map(({ companyId })=>companyId);
        const restricedPartnerCompanyIdsInProject = ((_ref2 = project === null || project === void 0 ? void 0 : project.partners) !== null && _ref2 !== void 0 ? _ref2 : []).filter(({ accessLevel })=>(accessLevel === null || accessLevel === void 0 ? void 0 : accessLevel.partnerPermissionSetName) === PartnerPermissionName.Restricted).map(({ companyId })=>companyId);
        const partnersWhoCanAccessPrivateResources = ((_ref3 = project === null || project === void 0 ? void 0 : project.partners) !== null && _ref3 !== void 0 ? _ref3 : []).filter(({ accessLevel })=>(accessLevel === null || accessLevel === void 0 ? void 0 : accessLevel.partnerPermissionSetName) === PartnerPermissionName.Permissive).map(({ companyId })=>companyId);
        const canBeInvitedByVendorOnPrivateResource = (user)=>user.userType === UserType.Native || user.userType === UserType.Partner && partnersWhoCanAccessPrivateResources.includes(user.company.companyId);
        const canBeInvitedByVendorOnSharedesource = (user)=>user.userType === UserType.Native || user.userType === UserType.Partner && partnersInProject.includes(user.company.companyId) && (options.includeRestricedPartnersMembers === true || !restricedPartnerCompanyIdsInProject.includes(user.company.companyId)) || user.userType === UserType.External && user.company.companyId === customerCompanyId;
        const canBeInvitedByVendor = (user)=>isPrivate ? canBeInvitedByVendorOnPrivateResource(user) : canBeInvitedByVendorOnSharedesource(user);
        const canBeInvitedByCustomer = (user)=>isPrivate ? false : user.company.companyId === customerCompanyId;
        const canBeInvitedByPartner = (user)=>{
            var _currentUserCompanyFromProjectPartners_accessLevel;
            if (currentUserCompanyFromProjectPartners == null) {
                return false;
            }
            if (isPrivate && ((_currentUserCompanyFromProjectPartners_accessLevel = currentUserCompanyFromProjectPartners.accessLevel) === null || _currentUserCompanyFromProjectPartners_accessLevel === void 0 ? void 0 : _currentUserCompanyFromProjectPartners_accessLevel.partnerPermissionSetName) !== PartnerPermissionName.Permissive) {
                return false;
            }
            return user.company.companyId === currentUser.company.companyId;
        };
        const canBeInvitedByCurrentUser = (user)=>{
            switch(currentUser.userType){
                case UserType.Native:
                    return canBeInvitedByVendor(user);
                case UserType.CustomerPartner:
                case UserType.External:
                case UserType.CustomerPortalBot:
                    return canBeInvitedByCustomer(user);
                case UserType.Partner:
                    return canBeInvitedByPartner(user);
                default:
                    return unreachable(currentUser.userType);
            }
        };
        return canBeInvitedByCurrentUser;
    }, [
        currentUser.company.companyId,
        currentUser.userType
    ]);
    return {
        getFilterUserInviteableInProject
    };
};
// if UserType === NATIVE then load all native users who are not in this project
// if UserType === EXTERNAL then load all cusatomer users who are not in this project
export function useGetAllMembersNotInProject({ projectId, isPrivate = false, options = {
    includeRestricedPartnersMembers: true,
    dontIncludeCustomers: false
}, ignoreRbacRules = false }) {
    const { data: project } = useProjectDetail(ProjectId(projectId !== null && projectId !== void 0 ? projectId : -1));
    const projectResource = useProjectAsRBACResource(projectId);
    const customerCompanyId = project === null || project === void 0 ? void 0 : project.customer.companyId;
    const { user: currentUser } = useUser();
    const { data: membersData, isLoading: isMembersLoading } = useMembersV2TransformedQuery();
    const currentUserCompanyFromProjectPartners = useMemo(()=>{
        var _ref;
        return currentUser.userType === UserType.Partner || currentUser.userType === UserType.CustomerPartner ? ((_ref = project === null || project === void 0 ? void 0 : project.partners) !== null && _ref !== void 0 ? _ref : []).find((partner)=>partner.companyId === currentUser.company.companyId) : undefined;
    }, [
        currentUser.company.companyId,
        currentUser.userType,
        project === null || project === void 0 ? void 0 : project.partners
    ]);
    const partnersInProject = useMemo(()=>{
        var _ref;
        return ((_ref = project === null || project === void 0 ? void 0 : project.partners) !== null && _ref !== void 0 ? _ref : []).map(({ companyId })=>companyId);
    }, [
        project === null || project === void 0 ? void 0 : project.partners
    ]);
    const restricedPartnerCompanyIdsInProject = useMemo(()=>{
        var _ref;
        return ((_ref = project === null || project === void 0 ? void 0 : project.partners) !== null && _ref !== void 0 ? _ref : []).filter(({ accessLevel })=>(accessLevel === null || accessLevel === void 0 ? void 0 : accessLevel.partnerPermissionSetName) === PartnerPermissionName.Restricted).map(({ companyId })=>companyId);
    }, [
        project === null || project === void 0 ? void 0 : project.partners
    ]);
    const partnersWhoCanAccessPrivateResources = useMemo(()=>{
        var _ref;
        return ((_ref = project === null || project === void 0 ? void 0 : project.partners) !== null && _ref !== void 0 ? _ref : []).filter(({ accessLevel })=>(accessLevel === null || accessLevel === void 0 ? void 0 : accessLevel.partnerPermissionSetName) === PartnerPermissionName.Permissive).map(({ companyId })=>companyId);
    }, [
        project === null || project === void 0 ? void 0 : project.partners
    ]);
    const canBeInvitedByVendorOnPrivateResource = useCallback((user)=>user.userType === UserType.Native || (user.userType === UserType.Partner || user.userType === UserType.CustomerPartner) && partnersWhoCanAccessPrivateResources.includes(user.company.companyId), [
        partnersWhoCanAccessPrivateResources
    ]);
    const canBeInvitedByVendorOnSharedesource = useCallback((user)=>user.userType === UserType.Native || (user.userType === UserType.Partner || user.userType === UserType.CustomerPartner) && partnersInProject.includes(user.company.companyId) && (options.includeRestricedPartnersMembers === true || !restricedPartnerCompanyIdsInProject.includes(user.company.companyId)) || user.userType === UserType.External && user.company.companyId === customerCompanyId, [
        customerCompanyId,
        options.includeRestricedPartnersMembers,
        partnersInProject,
        restricedPartnerCompanyIdsInProject
    ]);
    const canBeInvitedByVendor = useCallback((user)=>isPrivate ? canBeInvitedByVendorOnPrivateResource(user) : canBeInvitedByVendorOnSharedesource(user), [
        canBeInvitedByVendorOnPrivateResource,
        canBeInvitedByVendorOnSharedesource,
        isPrivate
    ]);
    const canBeInvitedByCustomer = useCallback((user)=>isPrivate ? false : user.company.companyId === customerCompanyId, [
        customerCompanyId,
        isPrivate
    ]);
    const canBeInvitedByCustomerPartner = useCallback((user)=>isPrivate ? false : user.company.companyId === currentUser.company.companyId, [
        isPrivate,
        currentUser
    ]);
    const canBeInvitedByPartner = useCallback((user)=>{
        var _currentUserCompanyFromProjectPartners_accessLevel;
        if (currentUserCompanyFromProjectPartners == null) {
            return false;
        }
        if (isPrivate && ((_currentUserCompanyFromProjectPartners_accessLevel = currentUserCompanyFromProjectPartners.accessLevel) === null || _currentUserCompanyFromProjectPartners_accessLevel === void 0 ? void 0 : _currentUserCompanyFromProjectPartners_accessLevel.partnerPermissionSetName) !== PartnerPermissionName.Permissive) {
            return false;
        }
        return user.company.companyId === currentUser.company.companyId;
    }, [
        currentUser.company.companyId,
        currentUserCompanyFromProjectPartners,
        isPrivate
    ]);
    const { hasAccessToResource } = useWithAccess();
    const { canInviteCustomerUsers, canInviteVendorUsers, canInvitePartnerUsers } = useMemo(()=>({
            canInviteVendorUsers: hasAccessToResource({
                rule: 'PROJECT_MEMBER:CREATE',
                resourceData: projectResource,
                actionScope: [
                    'NATIVE'
                ]
            }),
            canInviteCustomerUsers: hasAccessToResource({
                rule: 'PROJECT_MEMBER:CREATE',
                resourceData: projectResource,
                actionScope: [
                    'EXTERNAL'
                ]
            }),
            canInvitePartnerUsers: hasAccessToResource({
                rule: 'PROJECT_MEMBER:CREATE',
                resourceData: projectResource,
                actionScope: [
                    'PARTNER'
                ]
            })
        }), [
        hasAccessToResource,
        projectResource
    ]);
    const hasAccessToInviteThisUser = useCallback((user)=>{
        if (ignoreRbacRules) {
            return true;
        }
        switch(user.userType){
            case UserType.Native:
                return canInviteVendorUsers;
            case UserType.CustomerPartner:
            case UserType.External:
                return canInviteCustomerUsers;
            case UserType.Partner:
                return canInvitePartnerUsers;
            case UserType.CustomerPortalBot:
            default:
                return unreachable(user.userType);
        }
    }, [
        canInviteCustomerUsers,
        canInvitePartnerUsers,
        canInviteVendorUsers,
        ignoreRbacRules
    ]);
    const canBeInvitedByCurrentUser = useCallback((user)=>{
        switch(currentUser.userType){
            case UserType.Native:
                return canBeInvitedByVendor(user) && hasAccessToInviteThisUser(user);
            case UserType.External:
            case UserType.CustomerPortalBot:
                return canBeInvitedByCustomer(user) && hasAccessToInviteThisUser(user);
            case UserType.Partner:
                return canBeInvitedByPartner(user);
            case UserType.CustomerPartner:
                return canBeInvitedByCustomerPartner(user);
            default:
                return unreachable(currentUser.userType);
        }
    }, [
        currentUser.userType,
        canBeInvitedByVendor,
        hasAccessToInviteThisUser,
        canBeInvitedByCustomer,
        canBeInvitedByPartner,
        canBeInvitedByCustomerPartner
    ]);
    const conditionalFilter = useCallback((user)=>{
        var _user_projectIds;
        if (!projectId) {
            return false;
        }
        return !((_user_projectIds = user.projectIds) === null || _user_projectIds === void 0 ? void 0 : _user_projectIds.includes(projectId)) && canBeInvitedByCurrentUser(user);
    }, [
        canBeInvitedByCurrentUser,
        projectId
    ]);
    const data = useMemo(()=>{
        var _ref;
        if (!projectId) {
            return [];
        }
        // using membersData from cache because indexedDb is taking 20 seconds to return results
        return ((_ref = membersData === null || membersData === void 0 ? void 0 : membersData.users) !== null && _ref !== void 0 ? _ref : []).filter(conditionalFilter);
    }, [
        conditionalFilter,
        projectId,
        membersData
    ]);
    return {
        data: data || [],
        isLoading: isMembersLoading
    };
}
export function useGetAllMembersByCompany(companyId) {
    const { data: membersData } = useMembersV2TransformedQuery();
    return useMemo(()=>{
        var _ref;
        if (!companyId) {
            return [];
        }
        const data = ((_ref = membersData === null || membersData === void 0 ? void 0 : membersData.users) !== null && _ref !== void 0 ? _ref : []).filter((user)=>user.company.companyId === companyId);
        return data;
    }, [
        companyId,
        membersData === null || membersData === void 0 ? void 0 : membersData.users
    ]);
}
export function useGetAllTeamsFromIndexedDb() {
    const { data: membersData } = useMembersV2TransformedQuery();
    return useMemo(()=>{
        var _ref;
        const data = (_ref = membersData === null || membersData === void 0 ? void 0 : membersData.teams) !== null && _ref !== void 0 ? _ref : [];
        return data;
    }, [
        membersData === null || membersData === void 0 ? void 0 : membersData.teams
    ]);
}
export function useGetAllTeamsFromIndexedDbConditionally(projectId) {
    const { data: membersData } = useMembersV2TransformedQuery();
    return useMemo(()=>{
        var _ref;
        const data = ((_ref = membersData === null || membersData === void 0 ? void 0 : membersData.teams) !== null && _ref !== void 0 ? _ref : []).filter((team)=>{
            if (projectId) {
                var _team_projectIds_includes, _team_projectIds;
                return !!(team === null || team === void 0 ? void 0 : (_team_projectIds = team.projectIds) === null || _team_projectIds === void 0 ? void 0 : (_team_projectIds_includes = _team_projectIds.includes) === null || _team_projectIds_includes === void 0 ? void 0 : _team_projectIds_includes.call(_team_projectIds, projectId));
            }
            return !!(team === null || team === void 0 ? void 0 : team.accountLevelTeam);
        });
        return data;
    }, [
        membersData === null || membersData === void 0 ? void 0 : membersData.teams,
        projectId
    ]);
}
/**
 * @deprecated
 */ export function useGetUserFromIndexedDb(userId) {
    const { data: membersData } = useMembersV2TransformedQuery();
    return useMemo(()=>{
        var _ref;
        const data = ((_ref = membersData === null || membersData === void 0 ? void 0 : membersData.users) !== null && _ref !== void 0 ? _ref : []).find((user)=>user.userId === userId);
        return data !== null && data !== void 0 ? data : {};
    }, [
        membersData === null || membersData === void 0 ? void 0 : membersData.users,
        userId
    ]);
}
export const useGetUserFromProjectMembers = (projectId, userId)=>{
    const { data: projectMembers } = useProjectMembersQuery([
        'project_id',
        projectId
    ]);
    return useMemo(()=>{
        var _projectMembers_userList;
        const data = projectMembers === null || projectMembers === void 0 ? void 0 : (_projectMembers_userList = projectMembers.userList) === null || _projectMembers_userList === void 0 ? void 0 : _projectMembers_userList.find((user)=>user.userId === userId);
        return data !== null && data !== void 0 ? data : {};
    }, [
        projectMembers,
        userId
    ]);
};
export function useGetProjectIdsOfCurrentUserFromIndexedDb() {
    const { user: { userId } } = useUser();
    // Using query cache instead of IndexedDB, since useLiveQuery is causing unnecessary rerenders
    const { data: membersData } = useMembersV2TransformedQuery();
    const data = useMemo(()=>{
        var _ref;
        var _find, _this;
        return (_find = (_this = (_ref = membersData === null || membersData === void 0 ? void 0 : membersData.users) !== null && _ref !== void 0 ? _ref : []).find) === null || _find === void 0 ? void 0 : _find.call(_this, (x)=>x.userId === userId);
    }, [
        userId,
        membersData === null || membersData === void 0 ? void 0 : membersData.users
    ]);
    return (data === null || data === void 0 ? void 0 : data.projectIds) || [];
}
export function useGetProjectIdsOfUserFromIndexedDb(userId) {
    const data = useGetUserFromIndexedDb(userId);
    return (data === null || data === void 0 ? void 0 : data.projectIds) || [];
}
export function useGetUsersFromIndexedDb(userIds) {
    const { data: membersData } = useMembersV2TransformedQuery();
    return useMemo(()=>{
        var _ref;
        const data = ((_ref = membersData === null || membersData === void 0 ? void 0 : membersData.users) !== null && _ref !== void 0 ? _ref : []).filter((user)=>userIds.includes(user.userId));
        return data;
    }, [
        membersData === null || membersData === void 0 ? void 0 : membersData.users,
        userIds
    ]);
}
export function useGetExteralUsersOfCompanyFromIndexedDb(companyIds) {
    const { data: membersData } = useMembersV2TransformedQuery();
    return useMemo(()=>{
        var _ref;
        return ((_ref = membersData === null || membersData === void 0 ? void 0 : membersData.users) !== null && _ref !== void 0 ? _ref : []).filter((user)=>user.userType === UserType.External && companyIds.includes(user.company.companyId));
    }, [
        companyIds,
        membersData === null || membersData === void 0 ? void 0 : membersData.users
    ]);
}
export function useGetTeamsFromIndexedDb(teamIds) {
    const { data: membersData } = useMembersV2TransformedQuery();
    return useMemo(()=>{
        var _ref;
        const data = ((_ref = membersData === null || membersData === void 0 ? void 0 : membersData.teams) !== null && _ref !== void 0 ? _ref : []).filter((team)=>teamIds.includes(team.teamId));
        return data;
    }, [
        membersData === null || membersData === void 0 ? void 0 : membersData.teams,
        teamIds
    ]);
}
export function useFullName(userId) {
    const user = useGetUserFromIndexedDb(userId);
    return fullName(user);
}
async function getAllColumnOrderFromIndexedDb(orderType) {
    const data = await db.columnOrder.where({
        orderType
    }).toArray();
    return data;
}
export async function getColumnOrderFromIndexedDb(key) {
    const data = await db.columnOrder.where({
        key
    }).toArray();
    return data[0];
}
async function doesColumnOrderExist(key) {
    try {
        const data = await db.columnOrder.get(key);
        return data !== undefined;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
        return false;
    }
}
export async function addUpdateColumnOrderIndexedDb(key, viewId, columnOrder, orderType) {
    const doesExist = await doesColumnOrderExist(key);
    let payload = {
        key,
        viewId,
        columnOrder,
        orderType
    };
    if (doesExist) {
        const oldData = await db.columnOrder.get(key);
        payload = {
            ...payload,
            createdAt: oldData === null || oldData === void 0 ? void 0 : oldData.createdAt
        };
    } else {
        payload = {
            ...payload,
            createdAt: new Date().getTime()
        };
    }
    const data = await db.columnOrder.put(payload, key);
    return data;
}
export async function deleteColumnOrderFromIndexedDb(orderType, currentListViews) {
    const idbList = await getAllColumnOrderFromIndexedDb(orderType);
    if (idbList.length === 0 && currentListViews.ids[0] !== 'just') {
        return;
    }
    const deletedViewKeys = idbList.filter((x)=>{
        var _currentListViews_ids_, _currentListViews_ids;
        return currentListViews.ids[0] === 'just' && x.viewId >= 0 && !(currentListViews === null || currentListViews === void 0 ? void 0 : (_currentListViews_ids = currentListViews.ids) === null || _currentListViews_ids === void 0 ? void 0 : (_currentListViews_ids_ = _currentListViews_ids[1]) === null || _currentListViews_ids_ === void 0 ? void 0 : _currentListViews_ids_.includes(x.viewId));
    }).map((item)=>item.key);
    if (deletedViewKeys.length > 0) {
        await db.columnOrder.bulkDelete(deletedViewKeys);
    }
}
export async function getFormResponseDraftFromIndexedDb(formInstance) {
    const data = await db.formResponseDraft.where({
        formInstance
    }).toArray();
    return data[0];
}
export async function addUpdateFormResponseDraftIndexedDb(formInstance, response) {
    const data = await db.formResponseDraft.put({
        response,
        timestamp: moment().valueOf(),
        formInstance
    }, formInstance);
    return data;
}
export async function deleteFormResponseFromIndexedDb(formInstance) {
    return db.formResponseDraft.delete(formInstance);
}
export const useMembersInAndNotInProjects = ({ projectIds, isPrivate })=>{
    // TODO @Muskaan - debounce the computation
    const { data: membersData } = useMembersV2TransformedQuery();
    const { data: projectsInitData } = useProjectsFromInitData();
    const activeUsers = membersData === null || membersData === void 0 ? void 0 : membersData.users;
    const { getFilterUserInviteableInProject } = useFilterForUserInviteableInProject();
    const { data: projectLookup, isLoading } = useProjectPartnersMappingQuery(projectIds);
    return useMemo(()=>{
        if (isLoading || projectLookup == null || projectsInitData == null) {
            return {
                inProjectUsers: [],
                notInProjectUsers: []
            };
        }
        const usersBelongingToProjects = projectIds.reduce((previous, projectId)=>{
            var _ref;
            const projectWithpartner = projectLookup === null || projectLookup === void 0 ? void 0 : projectLookup[projectId];
            const permissivePartnerCompanyIdsInProject = ((_ref = projectWithpartner === null || projectWithpartner === void 0 ? void 0 : projectWithpartner.partners) !== null && _ref !== void 0 ? _ref : []).filter((partner)=>{
                var _partner_accessLevel;
                return ((_partner_accessLevel = partner.accessLevel) === null || _partner_accessLevel === void 0 ? void 0 : _partner_accessLevel.partnerPermissionSetName) === PartnerPermissionName.Permissive;
            }).map((partner)=>partner.companyId);
            const inProjectUser = (user)=>{
                var _user_projectIds;
                return (_user_projectIds = user.projectIds) === null || _user_projectIds === void 0 ? void 0 : _user_projectIds.includes(projectId);
            };
            const permissivePartnerUser = (user)=>permissivePartnerCompanyIdsInProject.includes(user.company.companyId);
            const assignableInPrivateResource = (user)=>user.userType === UserType.Native || permissivePartnerUser(user);
            const filterFunctionForInviteableUser = getFilterUserInviteableInProject(projectWithpartner, projectsInitData.byId[projectId], isPrivate);
            const inProjectUsers = (activeUsers !== null && activeUsers !== void 0 ? activeUsers : []).filter((user)=>inProjectUser(user) && (isPrivate ? assignableInPrivateResource(user) : true));
            const notInProjectUsers = (activeUsers !== null && activeUsers !== void 0 ? activeUsers : []).filter((user)=>!inProjectUser(user) && filterFunctionForInviteableUser(user));
            previous[projectId] = {
                inProjectUsers,
                notInProjectUsers
            };
            return previous;
        }, {});
        const usersInProjects = projectIds.reduce((previous, projectId)=>{
            const match = usersBelongingToProjects[projectId];
            if (match == null) {
                return previous;
            }
            return intersectionBy(previous, unionBy(match.inProjectUsers, match.notInProjectUsers, 'userId'), 'userId');
        }, activeUsers !== null && activeUsers !== void 0 ? activeUsers : []);
        const inProjectUsers = projectIds.reduce((previous, projectId)=>{
            var _ref;
            var _usersBelongingToProjects_projectId;
            return intersectionBy(previous, (_ref = (_usersBelongingToProjects_projectId = usersBelongingToProjects[projectId]) === null || _usersBelongingToProjects_projectId === void 0 ? void 0 : _usersBelongingToProjects_projectId.inProjectUsers) !== null && _ref !== void 0 ? _ref : [], 'userId');
        }, usersInProjects);
        const notInProjectUsers = differenceBy(usersInProjects, inProjectUsers, 'userId');
        return {
            inProjectUsers,
            notInProjectUsers
        };
    }, [
        activeUsers,
        getFilterUserInviteableInProject,
        isLoading,
        isPrivate,
        projectIds,
        projectLookup,
        projectsInitData
    ]);
};
export async function getTemplateDraftFromIndexedDb(templateId) {
    const data = await db.templateDraft.where({
        templateId
    }).toArray();
    const template = data === null || data === void 0 ? void 0 : data[0];
    if ((template === null || template === void 0 ? void 0 : template.templateType) === TemplateObjectType.Project) {
        return {
            ...template,
            content: migrations.addEntityOrderToProjectTemplatePhases(template.content)
        };
    }
    return template;
}
export async function addUpdateTemplateDraftIndexedDb(templateId, content, templateType) {
    const data = await db.templateDraft.put({
        content,
        timestamp: moment().valueOf(),
        templateId,
        templateType
    }, templateId);
    return data;
}
export async function deleteTemplateFromIndexedDb(templateIds) {
    return db.templateDraft.bulkDelete(templateIds);
}
export async function clearColumnOrderTableFromIndexedDb() {
    await db.columnOrder.clear();
}
